import React, { useEffect, useReducer, useState } from 'react'
import { segmentTrack } from '@src/functions/segment'
import { useRouter } from 'next/router'
import submitPPERequest from '@src/functions/request-ppe-quote'
import classNames from 'classnames'
import useEngagementTracker from '@src/hooks/useEngagementTracker'
import { destroyCart, syncCart } from '@src/functions/trackCart'
import classes from '../styles/QuoteRequestForm.module.css'

const REQUIRED_STEP_INFORMATION: any = {
  1: ['customerName', 'email', 'phoneNumber'],
  2: ['companyName', 'zipCode'],
  3: ['frequency'],
}

function formReducer(state: any, action: { type: string; payload: any }) {
  switch (action.type) {
    case 'SET_CUSTOMERNAME':
      return { ...state, customerName: action.payload }
    case 'SET_EMAIL':
      return { ...state, email: action.payload }
    case 'SET_PHONENUMBER':
      return { ...state, phoneNumber: action.payload }
    case 'SET_COMPANYNAME':
      return { ...state, companyName: action.payload }
    case 'SET_ZIPCODE':
      return { ...state, zipCode: action.payload }
    case 'SET_PALLETS':
      return { ...state, pallets: action.payload }
    case 'SET_DESCRIPTION':
      return { ...state, description: action.payload }
    case 'SET_MATERIALS':
      return { ...state, materials: action.payload }
    case 'SET_FREQUENCY':
      return { ...state, frequency: action.payload }
    default:
      throw new Error(`Unsupported action type: ${action.type}`)
  }
}

const initialState = {
  customerName: '',
  email: '',
  phoneNumber: '',
  companyName: '',
  zipCode: '',
  pallets: '',
  description: '',
  frequency: 'one-time',
  url: "getdimension.com",
  materials: [],
}

const formConverter = (data: any) => ({
  ...data,
  date: "",
  first_name: data.customerName?.split(" ")?.[0] ?? "",
  last_name: data.customerName?.split(" ")?.[1] ?? "",
  fullName: data.customerName,
  pickupAddress: {
    address: {},
    address_aditional: "",
  },
  pickupInstructions: "",
  plan: null,
  tos_checked: false,
  source: "getdimension.com",
})

function QuoteRequestFormV2({ isPallets = false }) {
  const history = useRouter()
  const [step, setStep] = useState(1)

  const [form, dispatch] = useReducer(formReducer, initialState)

  const [submitting, setSubmitting] = useState(false)

  const { engagementId } = useEngagementTracker({
    data: formConverter(form),
    meta: { step },
    context: isPallets ? "disposal-request" : "ppe-request",
    trigger: step,
    onSync: syncCart,
  });

  const handleMaterialChange = (e: any) => {
    const material = e.target.value
    const isSelected = e.target.checked

    if (isSelected) {
      dispatch({ type: 'SET_MATERIALS', payload: [...form.materials, material] })
    } else {
      const updatedMaterials = form.materials.filter((item: any) => item !== material)
      dispatch({ type: 'SET_MATERIALS', payload: updatedMaterials })
    }
  }

  // `Contact Entered` is a funnel signal: it marks that the visitor started
  // filling in contact details, and which fields they have completed so far. It
  // deliberately carries no name, email, phone or company name — this fires on
  // every field blur, so sending those would scatter identifying data across
  // analytics for a form the visitor may never submit. The real values still go
  // to the backend on submit, which is the only place they are needed.
  const handleBlur = (e: any) => {
    if (e.target.value) {
      segmentTrack('Contact Entered', {
        field: e.target.id,
        hasName: Boolean(form.customerName),
        hasEmail: Boolean(form.email),
        hasCompanyName: Boolean(form.companyName),
        hasPhone: Boolean(form.phoneNumber),
        hasZipCode: Boolean(form.zipCode),
        from: isPallets ? "Pallets Form": 'PPE Form',
        site: 'NextJS',
      })
    }
  }

  const handleInputChange = (event: any) => {
    const { id, value } = event.target
    dispatch({ type: `SET_${id.toUpperCase()}`, payload: value })
  }

  const validateFields = (fields: Array<string>) => {
    return fields.every((field) => Boolean(form[field]))
  }

  const handleStepChange = (stepNumber: number): any => {
    if (validateFields(REQUIRED_STEP_INFORMATION[step])) {
      setStep(stepNumber)
      switch (stepNumber) {
        case 2: {
          segmentTrack('Disposal Flow - Customer Info Submitted', {
            fullName: form.customerName ?? "",
            email: form.email ?? "",
            phone_number: form.phoneNumber ?? "",
            site: "getdimension.com",
          });
          break;
        }
        case 3: {
          segmentTrack('Disposal Flow - Additional Info Submitted', {
            companyName: form.companyName ?? "",
            zipCode: form.zipCode ?? "",
            site: "getdimension.com",
          });
          break;
        }
        default: {
          break;
        }
      };
    } else {
      // eslint-disable-next-line no-alert
      alert('Please make sure all required fields are filled out correctly!')
    }
  }

  const handleSubmit = async (e: any) => {
    e.preventDefault()
    setSubmitting(true)
    segmentTrack("Disposal Flow - Material Info Submitted", { pallets: form.pallets, frequency: form.frequency, materials: form.materials, description: form.description, site: "getdimension.com" })
    const formData = {
      isBusiness: true,
      ...form,
      url: "getdimension.com",
      pallets: Number(form.pallets),
      pathname: history.pathname,
    }
    const { success } = await submitPPERequest(formData)
    if (success) {
      segmentTrack(isPallets ? 'Pallet Quote Request Submitted': 'PPE Quote Request Submitted', formData)
      if (engagementId) {
        destroyCart(engagementId);
      }
      history.push(isPallets ? '/palletquote_success' : '/ppequote_success')
    } else {
      segmentTrack(isPallets ? 'Pallet Quote Request Failed' : 'PPE Quote Request Failed', formData)
      // eslint-disable-next-line no-alert
      alert('Please make sure all fields are filled out correctly!')
      setSubmitting(false)
    }
  }

  useEffect(() => {
    segmentTrack("Disposal Flow Started", { site: "getdimension.com" });
  }, []);

  return (
    <div className={classNames(classes.container, classes.containerShadow)}>
      <form className={classes.form} onSubmit={handleSubmit} autoComplete='on'>
        {step === 1 && (
          <>
            <label className={classes.formField} htmlFor='customerName'>
              Name
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='text'
                id='customerName'
                value={form.customerName}
                placeholder='John Doe'
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                autoComplete='name'
              />
            </label>
            <label className={classes.formField} htmlFor='email'>
              Email
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='email'
                id='email'
                value={form.email}
                placeholder='example@example.com'
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
              />
            </label>
            <label className={classes.formField} htmlFor='phoneNumber'>
              Phone Number
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='tel'
                id='phoneNumber'
                placeholder='(123) 456-7890'
                value={form.phoneNumber}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
              />
            </label>
          </>
        )}

        {step === 2 && (
          <>
            <label className={classes.formField} htmlFor='businessName'>
              What&apos;s the name of your business?
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='text'
                id='companyName'
                placeholder='Example Company'
                value={form.companyName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                autoComplete='organization'
              />
            </label>
            <label className={classes.formField} htmlFor='zipCode'>
              Please enter the zip code of the service address.
              <span className={classes.textRed} aria-hidden='true' role='presentation'>
                *
              </span>
              <input
                className={classes.input}
                type='text'
                id='zipCode'
                placeholder='12345'
                value={form.zipCode}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required
                autoComplete='postal-code'
              />
            </label>
          </>
        )}

        {step === 3 && (
          <>
            <div className={classes.complexFormField}>
              <span className={classes.complexFormFieldLabel}>
                Please select all the materials you wish to remove and receive a price quote for:
              </span>
              {!isPallets && (
                <>
                  <div className={classes.complexFormFieldsContainer}>
                    <label
                      className={`${classes.formField} ${classes.blueLight}`}
                      htmlFor='handSanitizer'
                    >
                      Hand Sanitizer
                      <input
                        type='checkbox'
                        id='handSanitizer'
                        value='Hand Sanitizer'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Hand Sanitizer')}
                      />
                    </label>
                    <label className={`${classes.formField} ${classes.cyanLight}`} htmlFor='masks'>
                      Masks
                      <input
                        type='checkbox'
                        id='masks'
                        value='Masks'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Masks')}
                      />
                    </label>
                    <label
                      className={`${classes.formField} ${classes.tealLight}`}
                      htmlFor='faceShields'
                    >
                      Face shields
                      <input
                        type='checkbox'
                        id='faceShields'
                        value='Face shields'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Face shields')}
                      />
                    </label>
                    <label className={`${classes.formField} ${classes.greenLight}`} htmlFor='detergent'>
                      Detergent
                      <input
                        type='checkbox'
                        id='detergent'
                        value='Detergent'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Detergent')}
                      />
                    </label>
                    <label
                      className={`${classes.formField} ${classes.yellowLight}`}
                      htmlFor='otherPPEs'
                    >
                      Other PPEs
                      <input
                        type='checkbox'
                        id='otherPPEs'
                        value='Other PPEs'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Other PPEs')}
                      />
                    </label>
                    <label
                      className={`${classes.formField} ${classes.orangeLight}`}
                      htmlFor='nonPPEWaste'
                    >
                      I also have non-PPE waste to remove
                      <input
                        type='checkbox'
                        id='nonPPEWaste'
                        value='Non-PPE Waste'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Non-PPE Waste')}
                      />
                    </label>
                  </div>
                </>
                )
              }
              {isPallets && (
                <>
                  <div className={classes.complexFormFieldsContainer}>
                    <label
                      className={`${classes.formField} ${classes.blueLight}`}
                      htmlFor='woodenPallets'
                    >
                      Wooden Pallets
                      <input
                        type='checkbox'
                        id='woodenPallets'
                        value='Wooden Pallets'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Wooden Pallets')}
                      />
                    </label>
                    <label className={`${classes.formField} ${classes.cyanLight}`} htmlFor='metalPallets'>
                      Metal Pallets
                      <input
                        type='checkbox'
                        id='metalPallets'
                        value='Metal Pallets'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Metal Pallets')}
                      />
                    </label>
                    <label
                      className={`${classes.formField} ${classes.tealLight}`}
                      htmlFor='Plastic Pallets'
                    >
                      Plastic Pallets
                      <input
                        type='checkbox'
                        id='plasticPallets'
                        value='Plastic Pallets'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Plastic Pallets')}
                      />
                    </label>
                    <label
                      className={`${classes.formField} ${classes.yellowLight}`}
                      htmlFor='otherPallets'
                    >
                      Other Pallets
                      <input
                        type='checkbox'
                        id='otherPallets'
                        value='Other Pallets'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Other Pallets')}
                      />
                    </label>
                    <label
                      className={`${classes.formField} ${classes.orangeLight}`}
                      htmlFor='nonPalletWaste'
                    >
                      I also have non-Pallet waste to remove
                      <input
                        type='checkbox'
                        id='nonPalletWaste'
                        value='Non-Pallet Waste'
                        onChange={handleMaterialChange}
                        checked={form.materials.includes('Non-Pallet Waste')}
                      />
                    </label>
                  </div>
                </>
                )
              }
            </div>
            <label className={classes.formField} htmlFor='pallets'>
              {isPallets ? 'At this moment how many pallets do you wish to remove immediately?' : 'How many pallets of materials do you wish to remove and receive a price quote?'}
              <input
                className={classes.input}
                type='number'
                id='pallets'
                value={form.pallets}
                placeholder='eg: 0'
                onChange={handleInputChange}
              />
            </label>
            <label className={classes.formField} htmlFor='description'>
              In one or two sentences, please briefly describe your quote request.
              <textarea
                className={classes.input}
                id='description'
                value={form.description}
                placeholder={isPallets ? 'eg: I have 10 wooden pallets, 15 metal pallets that I need to dispose of.' : 'eg: I have 3 pallets of hand sanitizer and 2 pallets of masks to remove.'}
                onChange={handleInputChange}
              />
            </label>
            {isPallets && (
              <label className={classes.formField} htmlFor='pallets'>
                Could you estimate what is the most likely frequency of similar needs? (One-Time, Daily, Weekly, Monthly)
                <select
                  className={classes.selectInput}
                  onChange={handleInputChange}
                  value={form.frequency}
                  name='frequency'
                  id='frequency'
                >
                  <option value='one-time'>One Time</option>
                  <option value='daily'>Daily</option>
                  <option value='weekly'>Weekly</option>
                  <option value='monthly'>Monthly</option>
                </select>
              </label>
            )}
          </>
        )}

        <div className={classes.buttonContainer}>
          {step > 1 && (
            <button className={classes.backButton} onClick={(e) => {
              e.preventDefault();
              setStep((prev) => prev - 1);
            }}>
              Back
            </button>
          )}
          {step >= 1 && step < 3 && (
            <button className={classes.submitButton} onClick={() => handleStepChange(step + 1)}>
              Next
            </button>
          )}
          {step === 3 && (
            <button className={classes.submitButton} type='submit' disabled={submitting}>
              {submitting ? 'Submitting' : 'Receive a price quote'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

export default QuoteRequestFormV2
